import {
  COMBINED_KILN_OPTION_VALUE,
  useEquipmentKilnKpisMtmStats,
  useEquipmentKilnKpisYtmStats,
  useEquipmentsKpisMtmStats,
  useEquipmentsKpisYtmStats,
  useHierarchy,
  usePlantEquipments
} from '@domain/kpis'
import {PxTrendMetaData, findLocationEntry} from '@domain/shared'
import {DataContentWrapper} from '@hconnect/common/containers'
import {PageContainer} from '@hconnect/uikit/src/lib2'
import {BaseErrorPage} from '@hconnect/uikit/src/lib2/pages/BaseErrorPage'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {spawnErrorToast} from '@hm/fundamental'
import {useState, useEffect, useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {EquipmentKpiStatsView, StatusPageHeader} from '../components'
import {DowntimeStatisticsViewToggleOptions, FEATURE_NAMESPACE} from '../consts'
import {useDowntimeStatisticsYtmMtmState, useEquipmentStatsDateRange} from '../hooks'
import {useDowntimePlantId} from '../providers'

const OTHER_EQUIPMENTS_OPTION_VALUE = 'other'

const isSelectedEquipmentEnabled = (selectedEquipmentId: string) => {
  return !!selectedEquipmentId && selectedEquipmentId !== COMBINED_KILN_OPTION_VALUE ? true : false
}

const isSelectedKilnEquipmentEnabled = (
  selectedEquipmentId: string,
  plantPxTrendMetaData?: PxTrendMetaData | undefined
) => {
  return !!selectedEquipmentId &&
    plantPxTrendMetaData &&
    !plantPxTrendMetaData.isGrindingUnit &&
    selectedEquipmentId === COMBINED_KILN_OPTION_VALUE
    ? true
    : false
}

const getSelectedEquipmentId = (selectedEquipmentId?: string) => {
  return selectedEquipmentId ?? ''
}

export const EquipmentKpiStats = () => {
  const plantId = useDowntimePlantId()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const navigate = useNavigate()
  const timezone = useUpmPlantTimezone(plantId)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string>('')

  const dateRange = useEquipmentStatsDateRange(timezone)
  const {data: hierarchy, isLoading: isHierarchyLoading} = useHierarchy()

  const handlePlantEquipmentsError = useCallback(() => {
    spawnErrorToast(t('equipmentSelection.message.failedToLoadData'))
  }, [t])
  const {data: plantEquipmentsData} = usePlantEquipments(plantId, {
    onError: handlePlantEquipmentsError
  })

  useEffect(() => {
    if (plantEquipmentsData?.length && !selectedEquipmentId) {
      setSelectedEquipmentId(plantEquipmentsData[0].equipmentNumber)
    }
  }, [plantEquipmentsData, selectedEquipmentId])

  const plantPxTrendMetaData = hierarchy
    ? findLocationEntry(hierarchy, plantId)?.pxTrendMetaData
    : undefined

  const enabledOtherEquipment = {
    enabled: isSelectedEquipmentEnabled(selectedEquipmentId)
  }

  const enabledKiln = {
    enabled: isSelectedKilnEquipmentEnabled(selectedEquipmentId, plantPxTrendMetaData)
  }

  const {kpiView} = useDowntimeStatisticsYtmMtmState()

  const {
    data: equipmentsKpisYtmStatsData,
    isLoading: isEquipmentsKpiYtmLoading,
    isError: isEquipmentsKpiYtmError,
    refetch: refetchEquipmentsKpiYtmStats
  } = useEquipmentsKpisYtmStats(
    plantId,
    dateRange,
    getSelectedEquipmentId(selectedEquipmentId),
    enabledOtherEquipment
  )

  const {
    data: equipmentsKpisMtmStatsData,
    isLoading: isEquipmentsKpiMtmLoading,
    isError: isEquipmentsKpiMtmError,
    refetch: refetchEquipmentsKpiMtmStats
  } = useEquipmentsKpisMtmStats(
    plantId,
    dateRange,
    getSelectedEquipmentId(selectedEquipmentId),
    enabledOtherEquipment
  )

  const {
    data: equipmentKilnKpisYtmStatsData,
    isLoading: isKilnYtmLoading,
    isError: isKilnYtmError,
    refetch: refetchKilnKpiYtmStats
  } = useEquipmentKilnKpisYtmStats(plantId, dateRange, enabledKiln)

  const {
    data: equipmentKilnKpisMtmStatsData,
    isLoading: isKilnMtmLoading,
    isError: isKilnMtmError,
    refetch: refetchKilnKpiMtmStats
  } = useEquipmentKilnKpisMtmStats(plantId, dateRange, enabledKiln)

  if (isHierarchyLoading) {
    return null
  }

  if (!plantPxTrendMetaData) return <BaseErrorPage caption={t('error.unsupportedPlant')} />

  const handleSelectedEquipmentChange = (equipmentId: string) => {
    setSelectedEquipmentId(equipmentId)
  }

  return (
    <PageContainer>
      <StatusPageHeader
        title={t('label.downtimeStats')}
        backButtonProps={{
          targetName: t('actions.backToDowntime'),
          onClick: () => navigate(-1)
        }}
      />
      {kpiView === DowntimeStatisticsViewToggleOptions.YTM_VIEW ? (
        <>
          {selectedEquipmentId === COMBINED_KILN_OPTION_VALUE ? (
            <DataContentWrapper
              showLoader={isKilnYtmLoading}
              retryFunction={refetchKilnKpiYtmStats}
              isError={isKilnYtmError}
              isNoData={false}
            >
              <EquipmentKpiStatsView
                data={equipmentKilnKpisYtmStatsData?.cockpitKpis || []}
                totalStopped={equipmentKilnKpisYtmStatsData?.totalStopped}
                isLoading={isKilnYtmLoading}
                timezone={timezone}
                selectedEquipmentId={selectedEquipmentId}
                onEquipmentChange={handleSelectedEquipmentChange}
                plantEquipmentsData={plantEquipmentsData}
                equipmentDataSource={COMBINED_KILN_OPTION_VALUE}
              />
            </DataContentWrapper>
          ) : equipmentsKpisYtmStatsData ? (
            <DataContentWrapper
              showLoader={isEquipmentsKpiYtmLoading}
              retryFunction={refetchEquipmentsKpiYtmStats}
              isError={isEquipmentsKpiYtmError}
              isNoData={false}
            >
              <EquipmentKpiStatsView
                data={equipmentsKpisYtmStatsData?.equipmentKpis || []}
                totalStopped={equipmentsKpisYtmStatsData.totalStopped}
                undefinedStoppageCount={equipmentsKpisYtmStatsData.undefinedStoppageCount}
                undefinedStoppageHours={equipmentsKpisYtmStatsData.undefinedStoppageHours}
                isLoading={isEquipmentsKpiYtmLoading}
                timezone={timezone}
                selectedEquipmentId={selectedEquipmentId}
                onEquipmentChange={handleSelectedEquipmentChange}
                plantEquipmentsData={plantEquipmentsData}
                equipmentDataSource={OTHER_EQUIPMENTS_OPTION_VALUE}
              />
            </DataContentWrapper>
          ) : null}
        </>
      ) : (
        <>
          {selectedEquipmentId === COMBINED_KILN_OPTION_VALUE ? (
            <DataContentWrapper
              showLoader={isKilnMtmLoading}
              retryFunction={refetchKilnKpiMtmStats}
              isError={isKilnMtmError}
              isNoData={false}
            >
              <EquipmentKpiStatsView
                data={equipmentKilnKpisMtmStatsData?.cockpitKpis || []}
                totalStopped={equipmentKilnKpisMtmStatsData?.totalStopped}
                isLoading={isKilnMtmLoading}
                timezone={timezone}
                selectedEquipmentId={selectedEquipmentId}
                onEquipmentChange={handleSelectedEquipmentChange}
                plantEquipmentsData={plantEquipmentsData}
                equipmentDataSource={COMBINED_KILN_OPTION_VALUE}
              />
            </DataContentWrapper>
          ) : equipmentsKpisMtmStatsData ? (
            <DataContentWrapper
              showLoader={isEquipmentsKpiMtmLoading}
              retryFunction={refetchEquipmentsKpiMtmStats}
              isError={isEquipmentsKpiMtmError}
              isNoData={false}
            >
              <EquipmentKpiStatsView
                data={equipmentsKpisMtmStatsData?.equipmentKpis || []}
                totalStopped={equipmentsKpisMtmStatsData.totalStopped}
                undefinedStoppageCount={equipmentsKpisMtmStatsData.undefinedStoppageCount}
                undefinedStoppageHours={equipmentsKpisMtmStatsData.undefinedStoppageHours}
                isLoading={isEquipmentsKpiMtmLoading}
                timezone={timezone}
                selectedEquipmentId={selectedEquipmentId}
                onEquipmentChange={handleSelectedEquipmentChange}
                plantEquipmentsData={plantEquipmentsData}
                equipmentDataSource={OTHER_EQUIPMENTS_OPTION_VALUE}
              />
            </DataContentWrapper>
          ) : null}
        </>
      )}
    </PageContainer>
  )
}
